import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Private route prefixes that must never be indexed by search engines.
// Keep in sync with EXCLUDE_PREFIXES in scripts/generate-sitemap.ts.
const PRIVATE_PREFIXES = [
  "/admin",
  "/portal",
  "/auth",
  "/refer/",
  "/review/",
  "/feedback/",
  "/unsubscribe",
  "/sms-consent",
  "/certification",
  "/apply",
  "/qr",
];

function isPrivate(pathname: string) {
  return PRIVATE_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p.endsWith("/") ? p : p + "/") || pathname === p,
  );
}

function isLovableHost() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h.endsWith(".lovable.app") || h.endsWith(".lovable.dev");
}

/**
 * Emits <meta name="robots" content="noindex,nofollow"> on private routes
 * (admin, customer portal, auth, token-based links). Keeps the public site
 * indexable while preventing crawlers from surfacing customer-facing tokens
 * or internal admin pages.
 *
 * Also emits noindex sitewide when served from any *.lovable.app host so the
 * staging/preview domain never competes with the canonical custom domain in
 * search results.
 */
/**
 * Direct-DOM noindex injector. We do NOT use react-helmet-async — it
 * is shimmed to a no-op so the external SEO editor stays the sole
 * authority over public-route head tags. For private routes (and any
 * Lovable preview host) we still need to keep crawlers out, so we
 * write the meta tag onto document.head ourselves and remove it when
 * the route becomes public again.
 */
function setMeta(name: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[name="${name}"][data-privacy-meta="1"]`,
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    el.setAttribute("data-privacy-meta", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function clearMeta() {
  document.head
    .querySelectorAll('meta[data-privacy-meta="1"]')
    .forEach((el) => el.remove());
}

export default function PrivacyMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (isPrivate(pathname) || isLovableHost()) {
      setMeta("robots", "noindex, follow");
      setMeta("googlebot", "noindex, follow");
      return () => clearMeta();
    }
    clearMeta();
  }, [pathname]);
  return null;
}